import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import type { ExportDesignFunction } from '@entities/mediumTemplates'
import { TracingUrlError } from '@pages/workflow/_shared'
import { useAppDispatch } from '@shared/lib/hooks'
import { openToast } from '@shared/model/slices'

type PreflightOptions = {
  /** 與實際操作一致：測試寄送會還原原始連結，儲存則否。 */
  shouldRecoverToOriginalLink?: boolean
}

/**
 * 在開啟「發送測試信設定」或「儲存範本命名」對話框「之前」，先把目前的 EDM 匯出並驗證一次。
 * - HTML 不合法（含 <script> / 過大…）時，onExportDesign 會自己跳具體的錯誤 toast，此 hook
 *   回傳 false → 呼叫端不要開啟對話框，使用者就不必填完整張表單／打完名字才被擋。
 * - 追蹤連結已封存時跳對應提示，同樣回傳 false。
 * - 一切正常才回傳 true。
 *
 * 傳入與「實際操作」相同的匯出參數（測試寄送 shouldRecoverToOriginalLink: true、儲存用預設），
 * 確保前置檢查與真正送出／儲存時的驗證結果一致。
 */
export const useEmailExportPreflight = (
  onExportDesign: ExportDesignFunction
) => {
  const dispatch = useAppDispatch()
  const { t } = useTranslation(['workflow'])

  return useCallback(
    async (options?: PreflightOptions): Promise<boolean> => {
      try {
        await onExportDesign({
          shouldExportPreviewUrl: false,
          shouldRecoverToOriginalLink: options?.shouldRecoverToOriginalLink,
        })
        return true
      } catch (error) {
        // HTML 驗證的具體訊息已由 onExportDesign dispatch，這裡只補追蹤連結的提示
        if (error instanceof TracingUrlError) {
          dispatch(
            openToast({
              message: t('workflow:errors.tracing_url_is_archived_plz_check'),
              status: 'error',
            })
          )
        }
        return false
      }
    },
    [onExportDesign, dispatch, t]
  )
}

export default useEmailExportPreflight
